/**
 * Renders a button shaped element without hover state or event handling.
 *
 * Provides support for multiple class sizes and rendering the rectangle with
 * or without rounded corners on any of the rectangles four corners.  This
 * feature is useful in implementing things such as segmented buttons or
 * dropdown controls.
 *
 * For advice on when to use this component instead of Button, see README.md.
 */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {borderRadius} from "@khan/shared-styles";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedIcon, {
    type IconType,
} from "#shared-styles-package/deprecated-icon.tsx";

import type {ButtonCorners, ButtonSize, Colors, Handlers} from "./types.ts";

type Props = Partial<Handlers> &
    Partial<Colors> & {
        children?: React.ReactNode;
        corners: ButtonCorners;
        size: ButtonSize;
        padding?: number;
        grow?: number;
        shrink?: number;
        weight?: "bold" | "normal";
        opacity?: number;
        icon?: string | IconType;
        // TODO(kevinb): support for different widths based on media queries
        width?: number | string;
        tag: "a" | "button" | "div";
        testId?: string;
        role?: string;
        disabled?: boolean;
        href?: string;
        target?: string;
        rel?: string;
        id?: string;
        ["aria-checked"]?: boolean;
        ["aria-haspopup"]?: boolean;
        ["aria-expanded"]?: boolean;
        ["aria-describedby"]?: string;
        ["aria-label"]?: string;
    };

type DefaultProps = {
    backgroundColor: string;
    borderColor: string;
    corners: ButtonCorners;
    size: ButtonSize;
    tag: "a" | "button" | "div";
    textColor: string;
};

export default class ButtonBase extends React.Component<Props> {
    tag: HTMLElement | null | undefined;

    static defaultProps: DefaultProps = {
        tag: "div",
        size: "default",
        textColor: "inherit",
        borderColor: "transparent",
        backgroundColor: "transparent",
        corners: "all",
    };

    focus() {
        if (this.tag != null) {
            this.tag.focus();
        }
    }

    getBoundingClientRect(): null | ClientRect {
        if (this.tag == null) {
            return null;
        }
        return this.tag.getBoundingClientRect();
    }

    render(): React.ReactNode {
        const {
            children,
            textColor,
            borderColor,
            backgroundColor,
            corners,
            padding,
            size,
            width,
            tag: Tag,
            grow,
            shrink,
            weight,
            opacity,
            icon,
            testId,
            ...otherProps // TODO(kevinb) limit this to handlers
        } = this.props;

        const dynamic = {
            color: textColor,
            borderColor: borderColor,
            backgroundColor: backgroundColor,
            // TODO(kevinb): make truncation configurable
            minWidth: width,
            flexGrow: 0,
        } as any;

        if (grow !== undefined) {
            dynamic.flexGrow = grow;
        }

        if (shrink !== undefined) {
            dynamic.flexShrink = shrink;
        }

        if (opacity !== undefined) {
            dynamic.opacity = opacity;
        }

        const innerDynamic = {} as any;
        if (padding) {
            innerDynamic.padding = `0 ${padding}px`;
        }

        const iconElem = icon && (
            <span
                className={css(
                    styles.iconContainer,
                    iconContainerSizeStyles[size],
                )}
            >
                <DeprecatedIcon color={dynamic.color} icon={icon} />
            </span>
        );

        const clear = {
            a: styles.link,
            button: styles.button,
        }[Tag];

        return (
            <Tag
                {...otherProps}
                className={css(
                    clear,
                    styles.common,
                    sizeStyles[size],
                    // 'corners' must come after 'sizes' because it has corrective
                    // vertical padding that must override the 0 padding from sizes
                    cornerStyles[corners],
                    // We can't dynamically override padding on the root element
                    // because it's already set as part of sizes so we
                    // disable it completely and set the specific padding value on
                    // the inner element.
                    padding !== undefined && styles.noPadding,
                    weight === "normal" && styles.normalWeight,
                    // If we're setting an icon, we need to increase the padding
                    // to make space for it.
                    icon && withIconStyles[size],
                )}
                style={dynamic}
                data-testid={testId}
                ref={(node) => (this.tag = node)}
            >
                {iconElem}

                <div className={css(styles.inner)} style={innerDynamic}>
                    {children}
                </div>
            </Tag>
        );
    }
}

const radius = borderRadius;

const styles = StyleSheet.create({
    common: {
        position: "relative",
        display: "inline-flex",
        fontFamily: "inherit",
        borderStyle: "solid",
        boxSizing: "border-box",
        alignItems: "center",
        flexShrink: 0, // prevent compression in flex layouts
        whiteSpace: "nowrap", // prevent line wrapping
        lineHeight: "normal", // consistent vertical positioning across tags
        fontWeight: "bold",

        cursor: "pointer",

        appearance: "none",
        WebkitTapHighlightColor: "transparent",
    },

    // horizontal centering of contents even if it's just text in a button
    inner: {
        verticalAlign: "middle",
        overflow: "hidden",
        textAlign: "center",
        textOverflow: "ellipsis",
        width: "100%",
    },

    noPadding: {
        padding: 0,
    },

    link: {
        textDecoration: "inherit",
    },

    button: {
        fontSize: "inherit",
        fontFamily: "inherit",
        padding: 0,
        margin: 0,
    },

    normalWeight: {
        fontWeight: "normal",
    },

    iconContainer: {
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
});

const sizeStyles = StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    xsmall: {
        height: 24,
        fontSize: 12,
        padding: "0px 8px",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    small: {
        height: 32,
        fontSize: 15,
        padding: "0px 12px",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    default: {
        height: 40,
        fontSize: 15,
        padding: "0px 16px",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    large: {
        height: 48,
        fontSize: 15,
        padding: "0px 20px",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    xlarge: {
        height: 60,
        fontSize: 18,
        lineHeight: "20px",
        fontWeight: 400,
    },
});

const withIconStyles = StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    xsmall: {
        padding: "0px 24px",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    small: {
        padding: "0px 32px",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    default: {
        padding: "0px 40px",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    large: {
        padding: "0px 48px",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    xlarge: {
        padding: "0px 56px",
    },
});

const iconContainerSizeStyles = StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    xsmall: {
        width: 24,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    small: {
        width: 32,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    default: {
        width: 40,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    large: {
        width: 48,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    xlarge: {
        width: 56,
    },
});

const cornerStyles = StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    top: {
        borderWidth: `1px 1px 0px 1px`,
        borderRadius: `${radius}px ${radius}px 0px 0px`,
        paddingBottom: 1,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    left: {
        borderWidth: `1px 0px 1px 1px`,
        borderRadius: `${radius}px 0px 0px ${radius}px`,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    bottom: {
        borderWidth: `0px 1px 1px 1px`,
        borderRadius: `0px 0px ${radius}px ${radius}px`,
        paddingTop: 1,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    right: {
        borderWidth: `1px 1px 1px 0px`,
        borderRadius: `0px ${radius}px ${radius}px 0px`,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    topLeft: {
        borderWidth: `1px 0px 0px 1px`,
        borderRadius: `${radius}px 0px 0px 0px`,
        paddingBottom: 1,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    topRight: {
        borderWidth: `1px 1px 0px 0px`,
        borderRadius: `0px ${radius}px 0px 0px`,
        paddingBottom: 1,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    bottomRight: {
        borderWidth: `0px 1px 1px 0px`,
        borderRadius: `0px 0px ${radius}px 0px`,
        paddingTop: 1,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    bottomLeft: {
        borderWidth: `0px 0px 1px 1px`,
        borderRadius: `0px 0px 0px ${radius}px`,
        paddingTop: 1,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    topCenter: {
        borderWidth: `1px 0px 0px 0px`,
        borderRadius: 0,
        paddingBottom: 1,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    rightCenter: {
        borderWidth: `0px 1px 0px 0px`,
        borderRadius: 0,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    bottomCenter: {
        borderWidth: `0px 0px 1px 0px`,
        borderRadius: 0,
        paddingTop: 1,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    leftCenter: {
        borderWidth: `1px 0px 0px 1px`,
        borderRadius: 0,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    all: {
        borderWidth: 1,
        borderRadius: 4,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    none: {},
});
